import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Grid,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../../context";
import useFetchObjects from "../../../../../api/useFetchObjects";
import useAdd from "../../../../../api/useAdd";
import { useQueryClient } from "@tanstack/react-query";

const MRoyDialog = ({ open, onClose, patientId, ticketId = null }) => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const queryClient = useQueryClient();

  const { data: testsData } = useFetchObjects(["mri-tests"], "m-roy-tests/", token);

  const { handleAdd, loading, resetForm } = useAdd(
    "mri-records",
    token,
    t("profile.mriAdded") || "MRI record created successfully",
    t("profile.mriAddFailed") || "Failed to create MRI record"
  );

  const [formData, setFormData] = useState({
    test: null,
    amount: "",
    discount: "",
    date: new Date().toISOString().split("T")[0],
    test_result: "",
    notes: "",
  });
  const [errors, setErrors] = useState({});

  const tests = testsData?.results || testsData || [];

  useEffect(() => {
    if (resetForm) {
      setFormData({
        test: null,
        amount: "",
        discount: "",
        date: new Date().toISOString().split("T")[0],
        test_result: "",
        notes: "",
      });
      setErrors({});
      onClose();
      queryClient.invalidateQueries(["unified-ehr", patientId]);
    }
  }, [resetForm, onClose, queryClient, patientId]);

  const handleSubmit = () => {
    const newErrors = {};
    if (!formData.test) newErrors.test = t("profile.mriTypeRequired") || "MRI test is required";
    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      return;
    }

    const amount = formData.amount === "" ? 0 : Number(formData.amount);
    const discount = formData.discount === "" ? 0 : Number(formData.discount);
    const safeAmount = Number.isFinite(amount) ? amount : 0;
    const safeDiscount = Number.isFinite(discount) ? discount : 0;
    const netAmount = Math.max(0, safeAmount - safeDiscount);

    handleAdd({
      patient: patientId,
      ticket: ticketId || null,
      test: formData.test,
      amount: netAmount,
      total_amount: netAmount,
      discount: safeDiscount,
      discount_amount: safeDiscount,
      date: formData.date,
      test_result: formData.test_result || "",
      notes: formData.notes || "",
      status: "Pending",
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t("profile.addMRI", "Add MRI")}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <Autocomplete
              options={tests}
              getOptionLabel={(option) => option.name || ""}
              value={tests.find((x) => x.id === formData.test) || null}
              onChange={(e, v) => {
                const testPrice = v ? (v.amount ?? v.price ?? "") : "";
                setFormData({ ...formData, test: v?.id || null, amount: testPrice });
                if (errors.test) setErrors({ ...errors, test: "" });
              }}
              renderInput={(params) => (
                <TextField {...params} label={t("profile.test", "Test")} error={!!errors.test} helperText={errors.test} required />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label={t("profile.amount", "Amount")} value={formData.amount} size="small" InputProps={{ readOnly: true }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth type="number" label={t("profile.discount", "Discount")} value={formData.discount} onChange={(e) => setFormData({ ...formData, discount: e.target.value })} size="small" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth type="date" label={t("profile.date") || "Date"} InputLabelProps={{ shrink: true }} value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} size="small" />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label={t("profile.testAppointment", "Test Appointment")} value={formData.test_result} onChange={(e) => setFormData({ ...formData, test_result: e.target.value })} size="small" />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth multiline minRows={3} label={t("profile.notes", "Notes")} value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} size="small" />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>{t("profile.cancel") || "Cancel"}</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? t("profile.saving") || "Saving..." : t("profile.add") || "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MRoyDialog;
